import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calculator, Loader2, CheckSquare, Square } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import DOMPurify from "dompurify";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SITUATIONS = [
  { id: "renter", label: "Renting" },
  { id: "homeowner", label: "Homeowner" },
  { id: "first-home", label: "First Home Buyer" },
  { id: "student", label: "Student" },
  { id: "parent", label: "Parent with Kids" },
  { id: "worker", label: "Employed Worker" },
  { id: "small-biz", label: "Small Business Owner" },
  { id: "retiree", label: "Pensioner / Retiree" },
  { id: "farmer", label: "Farmer / Rural" },
];

const CONCERNS = [
  { id: "housing", label: "Housing Affordability" },
  { id: "healthcare", label: "Healthcare & Medicare" },
  { id: "cost-of-living", label: "Cost of Living" },
  { id: "climate", label: "Climate & Environment" },
  { id: "jobs", label: "Jobs & Economy" },
  { id: "education", label: "Education" },
  { id: "tax", label: "Tax Policy" },
  { id: "immigration", label: "Immigration" },
  { id: "regional", label: "Regional Services" },
  { id: "defence", label: "National Security" },
];

function renderMd(text) {
  if (!text) return "";
  return text
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n\n/g, "</p><p>")
    .replace(/\n/g, "<br/>");
}

function ToggleChip({ selected, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 border-2 border-black text-sm font-semibold transition-all hover:-translate-y-0.5"
      style={{
        backgroundColor: selected ? "#2E5948" : "#fff",
        color: selected ? "#fff" : "#0F172A",
        boxShadow: "2px 2px 0px 0px #000",
      }}
    >
      {selected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
      {label}
    </button>
  );
}

export default function ImpactCalculatorPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [situations, setSituations] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  const toggle = (arr, set, id) =>
    set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const calculate = async () => {
    if (situations.length === 0 || concerns.length === 0) {
      setError("Please select at least one situation and one concern.");
      return;
    }
    setError("");
    setLoading(true);
    setResult("");
    try {
      const res = await axios.post(`${API}/ai/impact-calculator`, { situations, concerns });
      setResult(res.data.analysis);
    } catch {
      setError("Failed to generate analysis. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="impact-page">
      {/* Header */}
      <div className="py-12 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-4xl mx-auto">
          <button
            data-testid="impact-back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm font-bold mb-5"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          <div
            className="inline-block px-3 py-1 mb-3 text-xs font-bold tracking-widest border-2 uppercase"
            style={{ borderColor: "#FFC82E", color: "#FFC82E" }}
          >
            Impact Calculator
          </div>
          <h1
            className="text-4xl sm:text-5xl font-black text-white mb-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            How Policies Affect You
          </h1>
          <p className="text-base" style={{ color: "rgba(255,255,255,0.8)", fontFamily: "Public Sans, sans-serif" }}>
            Tell us about your life situation and what matters to you — AI will analyse how each party's policies would affect you personally.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10 space-y-8">
        {/* Step 1 */}
        <div
          className="border-2 border-black p-6"
          style={{ backgroundColor: surface, boxShadow: "4px 4px 0px 0px #000" }}
        >
          <h2
            className="font-black text-lg mb-1"
            style={{ fontFamily: "Outfit, sans-serif", color: text }}
          >
            Step 1 — Your Situation
          </h2>
          <p className="text-sm mb-4" style={{ color: textSec }}>
            Select all that apply to you.
          </p>
          <div className="flex flex-wrap gap-2">
            {SITUATIONS.map((s) => (
              <ToggleChip
                key={s.id}
                label={s.label}
                selected={situations.includes(s.id)}
                onClick={() => toggle(situations, setSituations, s.id)}
              />
            ))}
          </div>
        </div>

        {/* Step 2 */}
        <div
          className="border-2 border-black p-6"
          style={{ backgroundColor: surface, boxShadow: "4px 4px 0px 0px #000" }}
        >
          <h2
            className="font-black text-lg mb-1"
            style={{ fontFamily: "Outfit, sans-serif", color: text }}
          >
            Step 2 — Your Top Concerns
          </h2>
          <p className="text-sm mb-4" style={{ color: textSec }}>
            What issues matter most to you?
          </p>
          <div className="flex flex-wrap gap-2">
            {CONCERNS.map((c) => (
              <ToggleChip
                key={c.id}
                label={c.label}
                selected={concerns.includes(c.id)}
                onClick={() => toggle(concerns, setConcerns, c.id)}
              />
            ))}
          </div>
        </div>

        {/* Calculate */}
        {error && (
          <p className="text-sm font-medium" style={{ color: "#C04E3F" }}>
            {error}
          </p>
        )}
        <button
          data-testid="calculate-impact-btn"
          onClick={calculate}
          disabled={loading}
          className="flex items-center gap-3 px-8 py-4 text-white font-black text-base border-2 border-black hover:-translate-y-1 transition-transform disabled:opacity-60"
          style={{ backgroundColor: "#2E5948", boxShadow: "4px 4px 0px 0px #000" }}
        >
          {loading ? (
            <><Loader2 className="w-5 h-5 animate-spin" /> Calculating...</>
          ) : (
            <><Calculator className="w-5 h-5" /> Calculate My Policy Impact</>
          )}
        </button>

        {/* Results */}
        {loading && (
          <div
            className="border-2 border-black p-10 text-center"
            style={{ backgroundColor: surface, boxShadow: "4px 4px 0px 0px #000" }}
          >
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3" style={{ color: "#2E5948" }} />
            <p className="font-bold" style={{ color: text }}>Analysing how each party's policies affect your situation...</p>
            <p className="text-sm mt-1" style={{ color: textSec }}>Powered by GPT-4o</p>
          </div>
        )}

        {result && !loading && (
          <div
            className="border-2 border-black p-6"
            style={{ backgroundColor: surface, boxShadow: "4px 4px 0px 0px #000" }}
            data-testid="impact-result"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "#10B981" }} />
              <h3 className="font-black text-sm uppercase tracking-wider" style={{ color: textSec }}>
                AI Policy Impact Analysis
              </h3>
            </div>
            <div
              className="text-sm leading-relaxed"
              style={{ color: text, fontFamily: "Public Sans, sans-serif" }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`<p>${renderMd(result)}</p>`) }}
            />
            <p className="text-xs mt-4" style={{ color: textSec }}>
              This analysis is AI-generated for civic education purposes. Always verify with official party sources.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
